import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

const AutomationDashboard = () => {
  const [rules, setRules] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [activeTab, setActiveTab] = useState('rules');
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [lastExecution, setLastExecution] = useState(null);

  // Auto-refresh every 30 seconds
  const fetchData = useCallback(async () => {
    try {
      if (activeTab === 'rules') {
        const rulesResponse = await api.get('/marketing/automation/rules');
        setRules(rulesResponse.data.rules);
      } else {
        const executionsResponse = await api.get('/marketing/automation/executions');
        setExecutions(executionsResponse.data.executions);
      }
    } catch (error) {
      console.error('Error fetching automation data:', error);
    }
  }, [activeTab]);

  useEffect(() => {
    fetchData();
    setLoading(false);
    
    // Set up auto-refresh
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const executeAllRules = async () => {
    setExecuting(true);
    try {
      const response = await api.post('/marketing/automation/execute');
      setLastExecution(response.data);
      alert(`Automation completed! ${response.data.results.succeeded} rules succeeded.`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error executing automation:', error);
      alert('Automation execution failed');
    } finally {
      setExecuting(false);
    }
  };

  const executeSingleRule = async (ruleId, ruleName) => {
    try {
      const response = await api.post(`/marketing/automation/rules/${ruleId}/execute`);
      alert(`${ruleName} executed successfully! ${response.data.actions_taken} actions taken.`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error executing rule:', error);
      alert('Rule execution failed');
    }
  };

  const getRuleTypeIcon = (type) => {
    const icons = {
      lead_nurturing: '🎯',
      email_campaign: '📧',
      customer_lifecycle: '🔄',
      predictive_action: '🔮',
      content_optimization: '✍️',
      social_engagement: '📱'
    };
    return icons[type] || '⚙️';
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: '#10b981',
      running: '#f59e0b',
      failed: '#ef4444',
      pending: '#6b7280'
    };
    return colors[status] || '#6b7280';
  };

  const renderRules = () => (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        alignItems: 'center',
        marginBottom: '20px'
      }}>
        <button
          onClick={executeAllRules}
          disabled={executing}
          style={{
            backgroundColor: executing ? '#9ca3af' : 'var(--primary-600)',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '8px',
            cursor: executing ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500'
          }}
        >
          {executing ? '⏳ Executing...' : '🚀 Execute All Rules'}
        </button>
      </div>

      {lastExecution && (
        <div style={{ 
          backgroundColor: '#f0f9ff',
          border: '1px solid #0ea5e9',
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px'
        }}>
          <h4 style={{ margin: '0 0 8px 0', color: '#0369a1' }}>
            Last Execution Results
          </h4>
          <div style={{ fontSize: '14px', color: '#075985' }}>
            <p style={{ margin: '0 0 4px 0' }}>
              <strong>Total Rules:</strong> {lastExecution.results.executed} | 
              <strong style={{ color: '#059669', marginLeft: '8px' }}> Succeeded:</strong> {lastExecution.results.succeeded} | 
              <strong style={{ color: '#dc2626', marginLeft: '8px' }}> Failed:</strong> {lastExecution.results.failed}
            </p>
            <p style={{ margin: 0, fontStyle: 'italic' }}>
              {lastExecution.message}
            </p>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '16px' }}>
        {rules.map(rule => (
          <div
            key={rule.id}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '20px',
              backgroundColor: rule.is_active ? '#ffffff' : '#f9fafb'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '24px' }}>
                    {getRuleTypeIcon(rule.rule_type)}
                  </span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: '18px' }}>{rule.name}</h3>
                    <div style={{ fontSize: '14px', color: '#6b7280' }}>
                      {rule.rule_type.replace('_', ' ').toUpperCase()} | {rule.trigger_event}
                    </div>
                  </div>
                  <div style={{
                    backgroundColor: rule.is_active ? '#10b981' : '#ef4444',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}>
                    {rule.is_active ? 'ACTIVE' : 'INACTIVE'}
                  </div>
                </div>

                <p style={{ margin: '0 0 12px 0', fontSize: '14px', color: '#374151' }}>
                  {rule.description}
                </p>

                <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: '#6b7280' }}>
                  <div><strong>Executions:</strong> {rule.executions_count}</div>
                  <div><strong>Success Rate:</strong> {Math.round(rule.success_rate)}%</div>
                  {rule.last_execution && (
                    <div><strong>Last Run:</strong> {new Date(rule.last_execution).toLocaleString()}</div>
                  )}
                </div>
              </div>

              <div style={{ textAlign: 'right', minWidth: '150px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: 'var(--primary-600)' }}>
                      {rule.executions_count}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Total Runs</div>
                  </div>
                  <div>
                    <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>
                      {rule.success_count}
                    </div>
                    <div style={{ fontSize: '11px', color: '#6b7280' }}>Successes</div>
                  </div>
                </div>

                {rule.is_active && (
                  <button
                    onClick={() => executeSingleRule(rule.id, rule.name)}
                    style={{
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      width: '100%'
                    }}
                  >
                    Execute Now
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}

        {rules.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#6b7280',
            fontStyle: 'italic'
          }}>
            No automation rules configured yet.
          </div>
        )}
      </div>
    </div>
  );

  const renderExecutions = () => (
    <div>
      <h2>Automation Execution History</h2>
      <p style={{ color: '#6b7280', marginBottom: '20px' }}>
        View the history and results of automation rule executions
      </p>

      <div style={{ display: 'grid', gap: '12px' }}>
        {executions.map(execution => (
          <div
            key={execution.id}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '16px',
              backgroundColor: '#ffffff'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <h4 style={{ margin: 0, fontSize: '16px' }}>
                    {execution.rule_name}
                  </h4>
                  <div style={{
                    backgroundColor: getStatusColor(execution.status),
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    fontSize: '12px'
                  }}>
                    {execution.status.toUpperCase()}
                  </div>
                </div>

                <div style={{ fontSize: '12px', color: '#6b7280' }}>
                  Executed: {new Date(execution.execution_date).toLocaleString()}
                  {execution.completed_at && (
                    <span> | Completed: {new Date(execution.completed_at).toLocaleString()}</span>
                  )}
                </div>

                {execution.error_message && (
                  <div style={{ 
                    marginTop: '8px', 
                    padding: '8px', 
                    backgroundColor: '#fef2f2', 
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#b91c1c'
                  }}>
                    {execution.error_message}
                  </div>
                )}
              </div>

              <div style={{ textAlign: 'right', minWidth: '100px' }}>
                <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#10b981' }}>
                  {execution.actions_taken || 0}
                </div>
                <div style={{ fontSize: '12px', color: '#6b7280' }}>Actions Taken</div>
                
                {execution.success_count !== null && execution.success_count !== execution.actions_taken && (
                  <div style={{ marginTop: '4px' }}>
                    <div style={{ fontSize: '14px', color: '#f59e0b' }}>
                      {execution.success_count} succeeded
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {executions.length === 0 && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px', 
            color: '#6b7280',
            fontStyle: 'italic'
          }}>
            No automation executions yet. Run some rules to see activity here.
          </div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '400px' 
      }}>
        Loading automation dashboard...
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb Navigation */}
      <nav className="breadcrumb">
        <button
          onClick={() => window.location.href = '/'}
          className="breadcrumb-link"
        >
          Dashboard
        </button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">
          Marketing Automation
        </span>
      </nav>

      {/* Main Card */}
      <div className="card">
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1.5rem',
          paddingBottom: '1.5rem',
          borderBottom: '3px solid #EF4444'
        }}>
          <div>
            <h2 style={{
              color: 'var(--gray-900)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              margin: '0 0 0.25rem 0'
            }}>
              Marketing Automation Control Center
            </h2>
            <p style={{
              color: 'var(--gray-600)',
              margin: 0,
              fontSize: '0.875rem'
            }}>
              Manage and monitor automated marketing workflows
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div style={{
            display: 'flex',
            borderBottom: '2px solid var(--gray-200)',
            marginBottom: '0',
            gap: 'var(--space-1)'
          }}>
          <button
            onClick={() => setActiveTab('rules')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${activeTab === 'rules' ? 'var(--primary-600)' : 'transparent'}`,
              cursor: 'pointer',
              fontWeight: '600',
              color: activeTab === 'rules' ? 'var(--primary-600)' : 'var(--gray-500)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            ⚙️ Automation Rules ({rules.length})
          </button>
          <button
            onClick={() => setActiveTab('executions')}
            style={{
              padding: 'var(--space-3) var(--space-4)',
              background: 'none',
              border: 'none',
              borderBottom: `3px solid ${activeTab === 'executions' ? 'var(--primary-600)' : 'transparent'}`,
              cursor: 'pointer',
              fontWeight: '600',
              color: activeTab === 'executions' ? 'var(--primary-600)' : 'var(--gray-500)',
              transition: 'all 0.3s',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)'
            }}
          >
            📊 Execution History ({executions.length})
          </button>
          </div>
        </div>

        {activeTab === 'rules' ? renderRules() : renderExecutions()}
      </div>
    </div>
  );
};

export default AutomationDashboard;